import type {
  CandidateDiscoverySource,
  Component,
} from '@seek/data-tracking-event-catalogue';
import { useHubble } from '@seek/hubble';
import { useMelwaysInfo } from '@seek/melways-react';
import { useMemo } from 'react';

import type { TrackingConfig } from './trackingConfig';

interface TrackingMethods {
  trackAuthLinkPressedEvents: ({ component }: { component: Component }) => void;
  trackLoginEvents: ({
    component,
    identityProvider,
  }: {
    component: Component;
    identityProvider?: string;
  }) => void;
  trackComponentPressedEvent: ({ component }: { component: Component }) => void;
  trackComponentDisplayedEvent: ({
    component,
  }: {
    component: Component;
  }) => void;
  trackCandidateProfileDisplayedEvent: ({
    candidateDiscoverySource,
    candidateDiscoverySourceId,
  }: {
    candidateDiscoverySource: CandidateDiscoverySource;
    candidateDiscoverySourceId?: string;
  }) => void;
  trackProfileSearchDisplayedEvent: () => void;
  trackProfileSearchResultsDisplayedEvent: ({
    serviceToken,
  }: {
    serviceToken: string;
  }) => void;
}

export const useTracking = <T extends TrackingConfig>(
  configs: T,
): Record<keyof T, TrackingMethods> => {
  const hubble = useHubble();
  const { locale } = useMelwaysInfo();

  return useMemo(
    () =>
      Object.entries(configs).reduce(
        (acc, [key, { currentPage, currentPageSection }]) => ({
          ...acc,
          [key]: {
            trackAuthLinkPressedEvents: ({
              component,
            }: {
              component: Component;
            }) => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('auth_link_pressed', {
                displayLanguage: locale,
                currentPageSection,
                component,
              });
            },
            trackLoginEvents: ({
              component,
              identityProvider,
            }: {
              component: Component;
              identityProvider?: string;
            }) => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('login_pressed', {
                displayLanguage: locale,
                currentPageSection,
                component,
                identityProvider,
              });
            },
            trackComponentPressedEvent: ({
              component,
            }: {
              component: Component;
            }) => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('component_pressed', {
                displayLanguage: locale,
                currentPageSection,
                component,
              });
            },
            trackComponentDisplayedEvent: ({
              component,
            }: {
              component: Component;
            }) => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('component_displayed', {
                displayLanguage: locale,
                currentPageSection,
                component,
              });
            },
            trackCandidateProfileDisplayedEvent: ({
              candidateDiscoverySource,
              candidateDiscoverySourceId,
            }: {
              candidateDiscoverySource: CandidateDiscoverySource;
              candidateDiscoverySourceId?: string;
            }) => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('candidate_profile_displayed', {
                displayLanguage: locale,
                currentPageSection,
                candidateDiscoverySource,
                candidateDiscoverySourceId,
              });
            },
            trackProfileSearchDisplayedEvent: () => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('profile_search_displayed', {
                displayLanguage: locale,
                currentPageSection,
              });
            },
            trackProfileSearchResultsDisplayedEvent: ({
              serviceToken,
            }: {
              serviceToken: string;
            }) => {
              hubble.updateOptions({ pageName: currentPage });
              hubble.trackEvent('profile_search_results_displayed', {
                displayLanguage: locale,
                currentPageSection,
                serviceToken,
              });
            },
          },
        }),
        {} as Record<keyof T, TrackingMethods>,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hubble, locale],
  );
};
